import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, RotateCcw } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../components/ui/table";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const ESTADOS = [
	{ valor: "todos", rotulo: "Todos os estados" },
	{ valor: "pending", rotulo: "Na fila" },
	{ valor: "delivering", rotulo: "Entregando" },
	{ valor: "succeeded", rotulo: "Entregue" },
	{ valor: "failed", rotulo: "Falhou" },
	{ valor: "dead", rotulo: "Desistiu" },
];

const CORES = {
	succeeded: "default",
	dead: "destructive",
	failed: "destructive",
	pending: "secondary",
	delivering: "secondary",
};

const rotuloDoEstado = (estado) =>
	ESTADOS.find((e) => e.valor === estado)?.rotulo || estado;

const quando = (valor) =>
	valor ? new Date(valor).toLocaleString("pt-BR") : "—";

/**
 * Histórico de entregas de um endpoint.
 *
 * Mostra por item o código HTTP, a duração e a tentativa — não um "falhou"
 * genérico. Quem integra precisa saber se o destino recusou, demorou ou nem
 * respondeu, e cada um desses casos se conserta num lugar diferente.
 */
const PainelDeEntregas = ({ webhook, catalogo }) => {
	const [entregas, setEntregas] = useState([]);
	const [resumo, setResumo] = useState({ mortas: 0, sucesso24h: 0, total24h: 0 });
	const [pagina, setPagina] = useState(1);
	const [temMais, setTemMais] = useState(false);
	const [carregando, setCarregando] = useState(true);
	const [estado, setEstado] = useState("todos");
	const [evento, setEvento] = useState("todos");
	const [reenviando, setReenviando] = useState(null);

	const carregar = useCallback(async () => {
		setCarregando(true);
		try {
			const { data } = await api.get(`/webhooks/${webhook.id}/deliveries`, {
				params: {
					pageNumber: pagina,
					...(estado !== "todos" ? { status: estado } : {}),
					...(evento !== "todos" ? { event: evento } : {}),
				},
			});
			setEntregas(data.deliveries || []);
			setResumo(data.resumo || { mortas: 0, sucesso24h: 0, total24h: 0 });
			setTemMais(Boolean(data.hasMore));
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, [webhook.id, pagina, estado, evento]);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const reenviar = async (entrega) => {
		setReenviando(entrega.id);
		try {
			await api.post(
				`/webhooks/${webhook.id}/deliveries/${entrega.id}/redeliver`,
			);
			toast.success("Reenviada. Atualize em alguns segundos para ver o desfecho.");
			await carregar();
		} catch (err) {
			toastError(err);
		}
		setReenviando(null);
	};

	const taxa = resumo.total24h
		? Math.round((resumo.sucesso24h / resumo.total24h) * 100)
		: null;

	return (
		<div className="space-y-3 border-t bg-muted/30 p-4">
			<div className="flex flex-wrap items-end gap-3">
				<div>
					<Label className="text-xs">Estado</Label>
					<Select
						value={estado}
						onValueChange={(v) => {
							setPagina(1);
							setEstado(v);
						}}
					>
						<SelectTrigger className="w-44">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{ESTADOS.map((e) => (
								<SelectItem key={e.valor} value={e.valor}>
									{e.rotulo}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div>
					<Label className="text-xs">Evento</Label>
					<Select
						value={evento}
						onValueChange={(v) => {
							setPagina(1);
							setEvento(v);
						}}
					>
						<SelectTrigger className="w-56">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="todos">Todos os eventos</SelectItem>
							{catalogo.map((e) => (
								<SelectItem key={e.chave} value={e.chave}>
									{e.rotulo}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className="ml-auto flex gap-4 text-sm">
					<span>
						<span className="text-muted-foreground">Sucesso 24 h: </span>
						{taxa === null ? "sem entregas" : `${taxa}%`}
					</span>
					{resumo.mortas > 0 && (
						<span className="text-destructive">
							{resumo.mortas} desistiu
						</span>
					)}
				</div>
			</div>

			<div className="overflow-x-auto rounded-lg border bg-card">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Evento</TableHead>
							<TableHead>Estado</TableHead>
							<TableHead>HTTP</TableHead>
							<TableHead>Tentativa</TableHead>
							<TableHead>Quando</TableHead>
							<TableHead>Erro</TableHead>
							<TableHead className="text-right">Ações</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{carregando && (
							<TableRow>
								<TableCell colSpan={7} className="text-center text-sm">
									Carregando…
								</TableCell>
							</TableRow>
						)}

						{!carregando && !entregas.length && (
							<TableRow>
								<TableCell
									colSpan={7}
									className="py-6 text-center text-sm text-muted-foreground"
								>
									Nenhuma entrega com esse filtro. Use o botão Testar para
									disparar uma agora.
								</TableCell>
							</TableRow>
						)}

						{entregas.map((entrega) => (
							<TableRow key={entrega.id}>
								<TableCell className="font-mono text-xs">
									{entrega.event}
								</TableCell>
								<TableCell>
									<Badge variant={CORES[entrega.status] || "secondary"}>
										{rotuloDoEstado(entrega.status)}
									</Badge>
								</TableCell>
								<TableCell>{entrega.lastStatusCode ?? "—"}</TableCell>
								<TableCell>{entrega.attempts}</TableCell>
								<TableCell className="whitespace-nowrap text-xs">
									{quando(entrega.deliveredAt || entrega.createdAt)}
								</TableCell>
								<TableCell className="max-w-xs truncate text-xs text-muted-foreground">
									{entrega.lastError || "—"}
								</TableCell>
								<TableCell className="text-right">
									<Button
										variant="ghost"
										size="sm"
										disabled={
											!entrega.podeReenviar || reenviando === entrega.id
										}
										title={
											entrega.podeReenviar
												? "Reenviar"
												: "O corpo original deste evento já passou dos 30 dias de retenção"
										}
										onClick={() => reenviar(entrega)}
									>
										{reenviando === entrega.id ? (
											<Loader2 className="h-3.5 w-3.5 animate-spin" />
										) : (
											<RotateCcw className="h-3.5 w-3.5" />
										)}
										Reenviar
									</Button>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>

			{(pagina > 1 || temMais) && (
				<div className="flex justify-end gap-2">
					<Button
						variant="outline"
						size="sm"
						disabled={pagina === 1}
						onClick={() => setPagina((p) => p - 1)}
					>
						Anterior
					</Button>
					<Button
						variant="outline"
						size="sm"
						disabled={!temMais}
						onClick={() => setPagina((p) => p + 1)}
					>
						Próxima
					</Button>
				</div>
			)}
		</div>
	);
};

export default PainelDeEntregas;
